<template>
  <div
    :class="[
      isBottomPadded ? 'bottom-[88px] lg:bottom-4' : 'bottom-4',
      'fixed right-3 z-[10] flex flex-col gap-3 transition-all duration-300',
    ]"
  >
    <!-- Social CTA Trigger -->
    <div
      v-if="!['home', 'job-detail'].includes(route.name)"
      class="flex flex-col items-end gap-3 transition-all duration-300"
      @mouseenter="isExpanded = true"
      @mouseleave="isExpanded = false"
    >
      <TransitionGroup
        enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="opacity-0 translate-y-4 scale-90"
        enter-to-class="opacity-100 translate-y-0 scale-100"
        leave-active-class="transition-all duration-200 ease-in"
        leave-from-class="opacity-100 translate-y-0 scale-100"
        leave-to-class="opacity-0 translate-y-4 scale-90"
      >
        <div v-if="isExpanded" class="flex flex-col gap-3">
          <a
            href="https://wa.me/62895625026771"
            target="_blank"
            @click="trackContact('WhatsApp')"
            class="flex items-center justify-center bg-gradient-to-br from-green-500 to-emerald-600 text-white p-3 rounded-full shadow-lg hover:scale-110 transition-all duration-300 w-10 h-10 lg:w-10 lg:h-10"
          >
            <Icon icon="fa-brands:whatsapp" class="w-6 h-6 lg:w-7 lg:h-7" />
          </a>
          <a
            href="https://instagram.com/lokerjambi.co.id"
            target="_blank"
            @click="trackContact('Instagram')"
            class="flex items-center justify-center bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 text-white p-3 rounded-full shadow-lg hover:scale-110 transition-all duration-300 w-10 h-10 lg:w-10 lg:h-10"
          >
            <Icon icon="fa-brands:instagram" class="w-6 h-6 lg:w-7 lg:h-7" />
          </a>
        </div>
      </TransitionGroup>

      <button
        @click="isExpanded = !isExpanded"
        class="flex items-center justify-center bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 p-3 rounded-full shadow-lg border border-gray-200 dark:border-gray-700 hover:scale-110 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 w-10 h-10 lg:w-10 lg:h-10"
      >
        <Icon icon="mdi:headset" class="w-6 h-6 lg:w-7 lg:h-7" />
      </button>
    </div>

    <!-- Utility Buttons (Theme & Scroll) -->
    <button
      @click="toggleTheme"
      class="flex items-center justify-center bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 p-3 rounded-full shadow-lg border border-gray-200 dark:border-gray-700 hover:scale-110 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 w-10 h-10 lg:w-10 lg:h-10"
      aria-label="Toggle Theme"
    >
      <Icon :icon="isDark ? 'ph:sun-fill' : 'ph:moon-fill'" class="w-6 h-6 lg:w-7 lg:h-7" />
    </button>

    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-4"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-4"
    >
      <button
        v-if="showScrollTop"
        @click="scrollToTop"
        class="flex items-center justify-center bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 p-3 rounded-full shadow-lg border border-gray-200 dark:border-gray-700 hover:scale-110 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 w-10 h-10 lg:w-10 lg:h-10"
        aria-label="Scroll to Top"
      >
        <Icon icon="ph:arrow-up-bold" class="w-6 h-6 lg:w-7 lg:h-7" />
      </button>
    </Transition>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { Icon } from '@iconify/vue';
import { event as gtagEvent } from 'vue-gtag';

const route = useRoute();

defineProps({
  isBottomPadded: {
    type: Boolean,
    default: false,
  },
});

const isExpanded = ref(false);
const isDark = ref(false);
const showScrollTop = ref(false);

const trackContact = (channel) => {
  gtagEvent('contact_admin', {
    channel: channel,
    source: 'floating_button',
  });
};

const toggleTheme = () => {
  isDark.value = !isDark.value;
  document.documentElement.classList.toggle('dark', isDark.value);
  localStorage.setItem('theme', isDark.value ? 'dark' : 'light');
};

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const handleScroll = () => {
  showScrollTop.value = window.scrollY > 300;
};

onMounted(() => {
  const savedTheme = localStorage.getItem('theme');
  isDark.value =
    savedTheme === 'dark' ||
    (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.classList.toggle('dark', isDark.value);
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});
</script>
